import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { axiosKK } from '../../services/networkRequest';
import {
  CreateOrderIssueRequest,
  OrderIssueDTO,
  OrderIssueType,
} from '../../models/APIModels';
import errorMessage from '../../services/errorMessage';
import SubmitButton from '../SubmitButton';
import Label from '../Label';

interface Props {
  show: boolean;
  orderId: number;
  close: () => void;
  onSuccess?: (issue: OrderIssueDTO) => void;
}

const TYPES: { value: OrderIssueType; label: string }[] = [
  { value: 'damaged', label: 'Damaged' },
  { value: 'overweight', label: 'Overweight' },
  { value: 'repack', label: 'Repack' },
  { value: 'other', label: 'Other' },
];

export default function IssueModal({ show, orderId, close, onSuccess }: Props) {
  const [type, setType] = useState<OrderIssueType>('damaged');
  const [bid, setBid] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [netError, setNetError] = useState<string>();

  useEffect(() => {
    if (!show) return;
    setType('damaged');
    setBid('');
    setNote('');
    setNetError(undefined);
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [show, close]);

  if (!show) return null;

  const submit = () => {
    setSubmitting(true);
    setNetError(undefined);
    const payload: CreateOrderIssueRequest = { type };
    if (bid.trim()) payload.bid = bid.trim();
    if (note.trim()) payload.note = note.trim();

    axiosKK
      .post<OrderIssueDTO>(`/logistics/orders/${orderId}/issues`, payload)
      .then(({ data }) => {
        onSuccess?.(data);
        close();
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setSubmitting(false));
  };

  return ReactDOM.createPortal(
    <div className="logistics-modal__overlay" onClick={close}>
      <div className="logistics-modal__container" onClick={(e) => e.stopPropagation()}>
        <div className="logistics-modal__form">
          <div className="logistics-modal__header">
            <div className="logistics-modal__header-info">
              <h3>Flag Issue</h3>
              <span className="logistics-modal__subtitle">Order #{orderId}</span>
            </div>
            <button type="button" className="logistics-modal__close" onClick={close}>
              &times;
            </button>
          </div>
          <div className="logistics-modal__body">
            <div className="logistics-modal__field">
              <Label htmlFor="issue-type">Type</Label>
              <select
                id="issue-type"
                className="logistics-modal__select"
                value={type}
                onChange={(e) => setType(e.target.value as OrderIssueType)}
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="logistics-modal__field">
              <Label htmlFor="issue-bid">BID (optional)</Label>
              <input
                id="issue-bid"
                className="logistics-modal__input"
                type="text"
                value={bid}
                onChange={(e) => setBid(e.target.value)}
              />
            </div>
            <div className="logistics-modal__field">
              <Label htmlFor="issue-note">Note (optional)</Label>
              <textarea
                id="issue-note"
                className="logistics-modal__input"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            {netError && <span className="error">{netError}</span>}
          </div>
          <div className="logistics-modal__footer">
            <SubmitButton onClick={submit} isSubmitting={submitting} disabled={submitting}>
              Flag Issue
            </SubmitButton>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
